// pharma-sim-v0 reader: engagement maps (`files.maps` descriptors; `[parcel, target]` snapshots or
// `[frame, parcel, target]`), PK and occupancy series (`files.series`, `[frame, channel]`) and the
// header's per-target predictions. A zero mask means missing, never zero engagement.

import type { Typed } from './binary.ts';
import type { SimFiles } from './files.ts';
import { axisFromHeader, type TimeAxis } from './time.ts';
import type { PharmaHeader, PharmaMapDescriptor, PharmaPrediction, PharmaSeriesDescriptor } from './types.ts';

export interface EngagementMap {
  desc: PharmaMapDescriptor;
  /** `[frame][location][target]`; a snapshot has one frame. NaN where the mask says missing. */
  values: Float32Array;
  frames: number;
  locations: number;
  targets: string[];
}

export interface PkSeries {
  desc: PharmaSeriesDescriptor;
  quantity: string;
  units: string;
  channels: string[];
  /** `[frame][channel]`, NaN where masked. */
  values: Float32Array;
}

export interface BindingCurve {
  target: string;
  /** Inhibition constant (nM): central, with the low–high spread the header records. */
  kiNm: { low: number; central: number; high: number };
  /** Occupancy at the scenario's exposure, as the header predicts it. */
  occupancy: { low: number; central: number; high: number } | null;
}

const channelName = (c: PharmaSeriesDescriptor['channels'][number]) => (typeof c === 'string' ? c : (c.name ?? [c.compound, c.target].filter(Boolean).join(' · ')));

export class PharmaSim {
  readonly axis: TimeAxis;

  private constructor(
    readonly files: SimFiles,
    readonly header: PharmaHeader,
  ) {
    this.axis = axisFromHeader(header.time);
  }

  static async open(files: SimFiles, header: PharmaHeader): Promise<PharmaSim> {
    return new PharmaSim(files, header);
  }

  /** A one-frame snapshot names its reference; it must not animate a PK course. */
  get snapshot(): boolean {
    return !!this.header.time?.snapshot || this.axis.frames <= 1;
  }

  get name(): string {
    return this.header.name ?? this.header.drug ?? this.header.id;
  }

  get targets(): string[] {
    return this.header.target_codes ?? [];
  }

  get primaryTarget(): string | null {
    const p = this.header.primary_target;
    return p && this.targets.includes(p) ? p : (this.targets[0] ?? null);
  }

  /** Parcel order: `structure_ids` (Schaefer IDs are local to the receptor atlas until a crosswalk exists). */
  get structureIds(): string[] {
    return this.header.structure_ids ?? [];
  }

  async mapDescriptors(): Promise<PharmaMapDescriptor[]> {
    return Promise.all((this.header.files?.maps ?? []).map((p) => this.files.json<PharmaMapDescriptor>(p)));
  }

  /**
   * The map for a domain and bound, masked to NaN. With `quantity`, only a map of that quantity (or
   * one that names none): a comparison atlas's `raw` PET values aren't engagement on the 0–1 scale.
   */
  async map(domain = 'parcel', bound = 'central', quantity?: string): Promise<EngagementMap | null> {
    const paths = this.header.files?.maps ?? [];
    for (const p of paths) {
      const desc = await this.files.json<PharmaMapDescriptor>(p);
      if (desc.domain !== domain || (desc.bound ?? 'central') !== bound) continue;
      if (quantity && desc.quantity !== undefined && desc.quantity !== quantity) continue;
      const at = this.files.at(p);
      const raw = (await at.array(desc)) as Float32Array;
      const values = Float32Array.from(raw);
      const timed = desc.time_axis === 0 && desc.shape.length === 3;
      const frames = timed ? desc.shape[0]! : 1;
      const locations = desc.shape[timed ? 1 : 0]!;
      const T = desc.shape[timed ? 2 : 1]!;
      if (T !== desc.target_codes.length) throw new Error(`${p}: ${T} target columns for ${desc.target_codes.length} codes`);
      if (domain === 'parcel' && this.structureIds.length && locations !== this.structureIds.length) throw new Error(`${p}: ${locations} parcels for ${this.structureIds.length} structure ids`);
      const mask = desc.mask && typeof desc.mask === 'object' ? ((await at.array(desc.mask)) as Typed) : null;
      if (mask) {
        // A static mask is [location][target]; it repeats over frames.
        const per = locations * T;
        if (mask.length !== per && mask.length !== values.length) throw new Error(`${p}: mask has ${mask.length} entries`);
        for (let i = 0; i < values.length; i++) if (!mask[mask.length === per ? i % per : i]) values[i] = NaN;
      }
      return { desc, values, frames, locations, targets: desc.target_codes };
    }
    return null;
  }

  /** One target's values per location at a frame (a column of the map). */
  static column(m: EngagementMap, target: string, frame = 0): Float32Array {
    const t = m.targets.indexOf(target);
    const out = new Float32Array(m.locations).fill(NaN);
    if (t < 0) return out;
    const T = m.targets.length;
    const base = Math.min(frame, m.frames - 1) * m.locations * T;
    for (let l = 0; l < m.locations; l++) out[l] = m.values[base + l * T + t]!;
    return out;
  }

  /** PK and occupancy series (`files.series`), each `[frame][channel]`. */
  async series(): Promise<PkSeries[]> {
    const out: PkSeries[] = [];
    for (const p of this.header.files?.series ?? []) {
      const desc = await this.files.json<PharmaSeriesDescriptor>(p);
      const at = this.files.at(p);
      const values = Float32Array.from((await at.array(desc)) as Float32Array);
      if (desc.shape[0] !== this.axis.frames) throw new Error(`${p}: ${desc.shape[0]} frames, the header has ${this.axis.frames}`);
      if (desc.mask && typeof desc.mask === 'object') {
        const mask = await at.array(desc.mask);
        for (let i = 0; i < values.length; i++) if (!mask[i]) values[i] = NaN;
      }
      out.push({ desc, quantity: desc.quantity, units: desc.units ?? this.header.units?.concentration ?? '', channels: desc.channels.map(channelName), values });
    }
    return out;
  }

  /** Per-target binding (Ki and predicted occupancy), for targets the model covers. */
  get binding(): BindingCurve[] {
    return (this.header.predictions ?? [])
      .map((p: PharmaPrediction) => {
        const c = p.affinity?.central_nm;
        if (!(c && c > 0)) return null;
        const o = p.occupancy;
        return {
          target: p.target,
          kiNm: { low: p.affinity?.low_nm ?? c, central: c, high: p.affinity?.high_nm ?? c },
          occupancy: o?.central !== undefined ? { low: o.low ?? o.central, central: o.central, high: o.high ?? o.central } : null,
        } satisfies BindingCurve;
      })
      .filter((b): b is BindingCurve => b !== null);
  }

  /**
   * Binding for every modelled target: the header's predictions, else (a PK course) the Ki summaries
   * in the run's `affinities.json`, the curation its occupancy series cite.
   */
  async bindingCurves(): Promise<BindingCurve[]> {
    const own = this.binding;
    if (own.length) return own;
    const aff = await this.files.maybeJson<{ bindings?: { target: string; admitted?: boolean; affinity_summary?: { low_nm?: number; central_nm?: number; high_nm?: number } }[] }>('affinities.json');
    const modelled = new Set(this.targets);
    return (aff?.bindings ?? [])
      .filter((b) => b.admitted !== false && (!modelled.size || modelled.has(b.target)) && (b.affinity_summary?.central_nm ?? 0) > 0)
      .map((b) => {
        const s = b.affinity_summary!;
        return { target: b.target, kiNm: { low: s.low_nm ?? s.central_nm!, central: s.central_nm!, high: s.high_nm ?? s.central_nm! }, occupancy: null };
      });
  }

  /** The scenario's unbound brain exposure (nM), central with its bounds. */
  get exposureNm(): { low: number; central: number; high: number } | null {
    const e = this.header.exposure_nm;
    return e?.central !== undefined ? { low: e.low ?? e.central, central: e.central, high: e.high ?? e.central } : null;
  }
}

/** A PK course read for display: concentrations by compartment, occupancy by target with its bounds. */
export interface PkCourse {
  /** Concentration series (`*_nm`, one channel each): brain unbound first, then plasma. */
  concentrations: { quantity: string; label: string; units: string; values: Float32Array }[];
  /** Occupancy `[frame][target]` for each bound present (central, low, high). */
  occupancy: { targets: string[]; central: Float32Array | null; low: Float32Array | null; high: Float32Array | null };
}

const CONC_LABEL: Record<string, string> = { brain_unbound_nm: 'Brain, unbound', plasma_unbound_nm: 'Plasma, unbound', plasma_total_nm: 'Plasma, total' };
const CONC_ORDER = Object.keys(CONC_LABEL);

/** Group a PK run's series: single-channel nM series as concentrations, `occupancy_fraction` by bound. */
export function pkCourse(series: PkSeries[]): PkCourse {
  const concentrations = series
    .filter((s) => s.channels.length === 1 && /_nm$/.test(s.quantity))
    .sort((a, b) => (CONC_ORDER.indexOf(a.quantity) + 1 || 99) - (CONC_ORDER.indexOf(b.quantity) + 1 || 99))
    .map((s) => ({ quantity: s.quantity, label: CONC_LABEL[s.quantity] ?? s.quantity.replace(/_nm$/, '').replace(/_/g, ' '), units: s.units || 'nM', values: s.values }));
  const occ = series.filter((s) => s.quantity === 'occupancy_fraction');
  const by = (bound: string) => occ.find((s) => (s.desc.bound ?? 'central') === bound) ?? null;
  const central = by('central');
  const targets = (central ?? occ[0])?.desc.channels.map((c) => (typeof c === 'string' ? c : (c.target ?? c.name ?? ''))) ?? [];
  return { concentrations, occupancy: { targets, central: central?.values ?? null, low: by('low')?.values ?? null, high: by('high')?.values ?? null } };
}

/** The first dose event as the trace names it: '200 µg oral'. Null when the header has none with a mass. */
export function doseLabel(h: PharmaHeader): string | null {
  const e = h.scenario?.events?.find((x) => x.type === 'dose' && typeof x.mass === 'number');
  if (!e) return null;
  const unit = typeof e.mass_unit === 'string' ? e.mass_unit.replace(/^ug$/, 'µg') : '';
  return [`${e.mass as number}${unit ? ` ${unit}` : ''}`, typeof e.route === 'string' ? e.route : ''].filter(Boolean).join(' ');
}

/** Equilibrium site binding, the model the header names: O = C / (C + Ki). */
export const occupancy = (cNm: number, kiNm: number) => (cNm <= 0 ? 0 : cNm / (cNm + kiNm));
